/**
 * Shared API Client
 * Axios instance configured for both React Native and Web
 */

import axios from 'axios';

// Detect environment
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

// Base URL configuration
const getBaseURL = () => {
  if (isReactNative) {
    // EXPO_PUBLIC_* vars are inlined at build time by Expo/Metro.
    // Defaults to the deployed backend so installable test builds (APK/TestFlight)
    // work off the dev machine's WiFi, unlike Expo Go's same-network dev flow.
    return process.env.EXPO_PUBLIC_API_URL || 'https://smartgrow-backend.onrender.com/api/v1';
  }
  return '/api/v1';
};

// Create axios instance
// 60s, not the usual few-seconds default: the Render free-tier backend spins down after
// inactivity, and the first request after that can take 30-50s+ to cold-start the container
// before it even reaches the app. A shorter timeout was cutting off exactly that first
// request (most visibly on login) well before the server had a chance to respond.
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Storage abstraction for both environments. Reads globalThis.__smartgrow_async_storage
// fresh on every call (not once at module-load) — App.tsx sets that global as a side
// effect of its own top-level code, and this module can be evaluated (e.g. transitively,
// via any screen that imports an api service) before or after that assignment runs
// depending on the exact import graph. Capturing it once here previously caused
// "Cannot read property 'getItem' of undefined" whenever this module happened to load
// first: the closure had already frozen in `undefined` and never re-checked the global.
const getStorageAdapter = () => {
  if (isReactNative) {
    return {
      getItem: (key: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AsyncStorage = (globalThis as any).__smartgrow_async_storage;
        return AsyncStorage.getItem(key);
      },
      setItem: (key: string, value: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AsyncStorage = (globalThis as any).__smartgrow_async_storage;
        return AsyncStorage.setItem(key, value);
      },
      removeItem: (key: string) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AsyncStorage = (globalThis as any).__smartgrow_async_storage;
        return AsyncStorage.removeItem(key);
      },
    };
  } else {
    return {
      getItem: (key: string) => Promise.resolve(localStorage.getItem(key)),
      setItem: (key: string, value: string) => Promise.resolve(localStorage.setItem(key, value)),
      removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
    };
  }
};

const storage = getStorageAdapter();

// Tiny pub/sub so non-React code (this axios interceptor) can tell React code
// (AuthContext on mobile) that the session just ended — e.g. a 401 came back
// and storage was cleared. Web doesn't need this (it hard-navigates instead),
// but mobile has no page reload to fall back on, so without this the app was
// stuck showing a "logged in" shell with no token and every request silently
// failing after storage got cleared.
type SessionEndedListener = () => void;
const sessionEndedListeners = new Set<SessionEndedListener>();
export const onSessionEnded = (listener: SessionEndedListener) => {
  sessionEndedListeners.add(listener);
  return () => { sessionEndedListeners.delete(listener); };
};
const emitSessionEnded = () => sessionEndedListeners.forEach((l) => l());

// Request interceptor - Add JWT token to requests
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await storage.getItem('jwt_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting token from storage:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle authentication errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      try {
        await storage.removeItem('jwt_token');
        await storage.removeItem('user_data');

        // Redirect to login based on environment
        if (!isReactNative && typeof window !== 'undefined') {
          window.location.href = '/login';
        } else {
          emitSessionEnded();
        }
      } catch (storageError) {
        console.error('Error clearing storage:', storageError);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Turn any error caught from an apiClient call into a message safe to show a user.
 * Distinguishes "server responded with an error" (has a real message worth showing,
 * or a sensible per-status fallback) from "request never reached the server" (network
 * down, DNS/TLS failure, timeout) — conflating the two is actively misleading, e.g.
 * showing "invalid credentials" for what was actually a dropped connection.
 */
export const getErrorMessage = (err: any, fallback = 'Something went wrong. Please try again.'): string => {
  if (!err?.response) {
    return err?.code === 'ECONNABORTED'
      ? 'Request timed out. Check your connection and try again.'
      : 'Could not reach the server. Check your internet connection and try again.';
  }
  return err.response?.data?.detail || err.response?.data?.message || fallback;
};

export default apiClient;
export { storage };
